import { createApi } from '@reduxjs/toolkit/query/react';
import {
  type FetchBaseQueryError,
  type QueryReturnValue,
} from '@reduxjs/toolkit/query';
import { waServicesQuery } from './baseQueries';
import { getLastSignedInAdvisorGroupId } from '@/utils/lastSignedInAdvisorGroupId';
import { clientsApi } from './clientsApi';
import type { WARootState } from './store';

export type ClientInvitationSalutation = 'firstName' | 'firstAndLastName';
export type ClientInvitationInviterType = 'firm' | 'advisor';

export interface ClientInvitationConfig {
  advisorGroupId: number | null;
  salutation: ClientInvitationSalutation;
  messageBody: string;
  signature: string;
  inviterType: ClientInvitationInviterType;
}

export interface UpdateClientInvitationConfigRequest {
  advisorGroupId: number | null;
  salutation: ClientInvitationSalutation;
  messageBody: string;
  signature: string;
  inviterType: ClientInvitationInviterType;
}

/** Raw shape returned/accepted by the backend */
interface ClientInvitationConfigApiResponse {
  Content?: string;
  CustomSignature?: string;
  UseLastName?: boolean;
  UseGroupName?: boolean;
  AdvisorGroupId?: number | null;
}

type BaseQueryResult<T> = QueryReturnValue<T, FetchBaseQueryError>;

function mapResponse(
  r: ClientInvitationConfigApiResponse,
): ClientInvitationConfig {
  return {
    advisorGroupId: r.AdvisorGroupId ?? null,
    salutation: r.UseLastName ? 'firstAndLastName' : 'firstName',
    messageBody: r.Content ?? '',
    signature: r.CustomSignature ?? '',
    inviterType: r.UseGroupName === false ? 'advisor' : 'firm',
  };
}

function mapRequest(
  config: UpdateClientInvitationConfigRequest,
): ClientInvitationConfigApiResponse {
  return {
    AdvisorGroupId: config.advisorGroupId ?? undefined,
    Content: config.messageBody,
    CustomSignature: config.signature,
    UseLastName: config.salutation === 'firstAndLastName',
    UseGroupName: config.inviterType === 'firm',
  };
}

export const clientInvitationConfigApi = createApi({
  reducerPath: 'clientInvitationConfigApi',
  baseQuery: waServicesQuery,
  tagTypes: ['ClientInvitationConfig'],
  endpoints: (builder) => ({
    getClientInvitationConfig: builder.query<ClientInvitationConfig, number>({
      queryFn: async (advisorGroupId, _queryApi, _extra, baseQuery) => {
        const result = (await baseQuery({
          url: `/api/firm/advisorGroups/${String(advisorGroupId)}/investorInviteSetting`,
          method: 'GET',
        })) as BaseQueryResult<ClientInvitationConfigApiResponse>;
        if (result.error) return { error: result.error };
        return { data: mapResponse(result.data) };
      },
      providesTags: ['ClientInvitationConfig'],
    }),
    updateClientInvitationConfig: builder.mutation<
      ClientInvitationConfig,
      UpdateClientInvitationConfigRequest
    >({
      queryFn: async (config, { getState, dispatch }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        // Prefer advisorGroupId from the request; fall back to localStorage
        const advisorGroupId =
          config.advisorGroupId !== null
            ? config.advisorGroupId
            : (getLastSignedInAdvisorGroupId() ??
              (
                state.appView.UserInfo as
                  | { AdvisorGroupId?: number | null }
                  | undefined
              )?.AdvisorGroupId ??
              null);

        if (!advisorGroupId) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: 'AdvisorGroupId not available',
            } as FetchBaseQueryError,
          };
        }

        const result = (await baseQuery({
          url: `/api/firm/advisorGroups/${String(advisorGroupId)}/investorInviteSetting`,
          method: 'PUT',
          body: mapRequest(config),
        })) as BaseQueryResult<ClientInvitationConfigApiResponse>;
        if (result.error) return { error: result.error };
        // The invite modal reads these same firm-level settings via clientsApi's
        // InviteSettings tag; invalidate it too so it doesn't serve stale content.
        dispatch(clientsApi.util.invalidateTags(['InviteSettings']));
        return { data: mapResponse(result.data) };
      },
      invalidatesTags: ['ClientInvitationConfig'],
    }),
  }),
});

export const {
  useGetClientInvitationConfigQuery,
  useUpdateClientInvitationConfigMutation,
} = clientInvitationConfigApi;
